// ============================================
// TARJETAVIGILANTE.JS — el aviso diario del vigilante, en Mi dia
// ============================================
// POR QUE (auditoria 2026-09-22): 135 expedientes abiertos sin ninguna cotizacion y 43
// ya comprando o facturando sin precio registrado. Nadie se entero en meses porque nada
// lo hacia visible.
//
// LA REGLA DE ORO DEL DISEÑO, y esta tarjeta existe para cumplirla: *un vigilante que
// avisa de todo se ignora en una semana*. Por eso:
//   - Se enseñan SOLO los 3 mas urgentes (lo fija direccion en los ajustes). El resto es
//     un contador con un "ver todos" detras. Una lista de 40 no se lee, se cierra.
//   - Primero lo ROJO (se esta moviendo dinero sin precio), luego lo ambar.
//   - Si ya la viste hoy, se pliega a UNA linea. No se repite el regaño.
//   - Un aviso no se puede quitar sin resolverlo: lo unico que se puede es POSPONERLO
//     con un motivo escrito, que queda registrado.
//
// Direccion ve ademas el resumen de la empresa: una FRASE por persona, no una tabla.
// Ese bloque solo aparece si el servidor lo da; el candado esta en el backend.

import React, { useCallback, useEffect, useState } from 'react';
import { API_BASE } from '../config/api';
import { authHeaders, storage, STORAGE_KEYS } from '../utils/storage';
import { fmtDinero } from '../utils/dinero';
import useResponsive from '../hooks/useResponsive';

const PP = "'Poppins', sans-serif";

const TONO = {
  rojo:  { fondo: '#fdecec', borde: '#f3c9c9', texto: '#8a2b2b', barra: '#c0392b', punto: '🔴' },
  ambar: { fondo: '#fff8ed', borde: '#fce5b8', texto: '#8a5a1b', barra: '#d79a2b', punto: '🟡' },
  gris:  { fondo: '#f4f5f6', borde: '#e0e3e6', texto: '#555d65', barra: '#9aa3ab', punto: '⚪' },
};

const abrirExpediente = (id) =>
  window.dispatchEvent(new CustomEvent('beep-abrir-expediente', { detail: { expedienteId: id } }));

// ALERTA-BAJO-COSTO: abrir la lista con precio congelado (o la coleccion) donde esta la linea.
// La pantalla destino lee la clave al montar y abre esa lista; el salto de pestaña es el mismo
// evento que usa el resto de la app.
const abrirLista = (l) => {
  storage.set(STORAGE_KEYS.LISTA_ABRIR, { id: l.id, nombre: l.nombre || '', congelada: l.congelada !== false });
  const tab = l.congelada === false ? 'cotizaciones.colecciones' : 'cotizaciones.listas';
  window.dispatchEvent(new CustomEvent('beep-ir-tab', { detail: { tab } }));
};

export default function TarjetaVigilante() {
  const [datos, setDatos] = useState(null);
  const [resumen, setResumen] = useState(null);
  const [plegada, setPlegada] = useState(false);
  const [todos, setTodos] = useState(null);
  const [posponiendo, setPosponiendo] = useState(null); // { id, folio }
  const [motivo, setMotivo] = useState('');
  const [dias, setDias] = useState(7);
  const [error, setError] = useState(null);
  const { isMobile } = useResponsive();

  const cargar = useCallback(async () => {
    try {
      const resp = await fetch(`${API_BASE}/api/vigilante/mis-avisos`, { headers: authHeaders() });
      if (!resp.ok) return;                       // sin sesion o sin permiso: la tarjeta calla
      const r = await resp.json();
      if (!r.ok) return;
      setDatos(r);
      setPlegada(!!r.ya_visto_hoy);               // punto 17: si ya la vio hoy, no se repite
      if (r.es_direccion) {
        const r2 = await fetch(`${API_BASE}/api/vigilante/resumen-empresa`, { headers: authHeaders() })
          .then(x => (x.ok ? x.json() : null)).catch(() => null);
        if (r2 && r2.ok) setResumen(r2);
      }
    } catch { /* sin red: la tarjeta no aparece */ }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const marcarVisto = async () => {
    setPlegada(true);
    try { await fetch(`${API_BASE}/api/vigilante/visto`, { method: 'POST', headers: authHeaders() }); }
    catch { /* best-effort */ }
  };

  const verTodos = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/vigilante/mis-avisos/todos`, { headers: authHeaders() })
        .then(x => x.json());
      if (r.ok) setTodos(r.mios.items);
    } catch { /* nada */ }
  };

  const confirmarPosponer = async () => {
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/vigilante/avisos/${posponiendo.id}/posponer`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ dias: Number(dias), motivo }),
      }).then(x => x.json());
      if (!r.ok) { setError(r.error); return; }
      setPosponiendo(null); setMotivo(''); setDias(7); setTodos(null);
      cargar();
    } catch { setError('No se pudo guardar. Intenta otra vez.'); }
  };

  if (!datos) return null;
  const mios = datos.mios || { total: 0, cuenta: {}, items: [] };
  const huer = datos.huerfanos;
  const nadaPropio = mios.total === 0;
  const nadaHuerfano = !huer || huer.total === 0;
  const nadaEmpresa = !resumen || !resumen.totales || resumen.totales.total === 0;
  // ALERTA-BAJO-COSTO: lo que se vende bajo costo SIN revisar es ROJO. Lo ya aceptado no avisa.
  const lbc = datos.listas_bajo_costo;
  const listasRojas = lbc && lbc.margen_resumen ? (lbc.margen_resumen.bajo_costo || 0) : 0;
  const nadaListas = !lbc || (!listasRojas && !(lbc.margen_resumen && lbc.margen_resumen.margen_bajo));
  if (nadaPropio && nadaHuerfano && nadaEmpresa && nadaListas) return null;   // sin nada que decir, no molesta

  const hayRojo = (mios.cuenta.rojo || 0) > 0 || listasRojas > 0;
  const tono = hayRojo ? TONO.rojo : TONO.ambar;

  const boton = {
    padding: '7px 12px', minHeight: 40, borderRadius: 8, cursor: 'pointer', fontFamily: PP,
    fontSize: 12.5, fontWeight: 600, border: `1px solid ${tono.borde}`, background: '#fff', color: tono.texto,
  };

  // --- plegada: UNA linea, sin regaño ---
  if (plegada) {
    return (
      <div
        onClick={() => setPlegada(false)}
        style={{
          background: '#fff', border: `1px solid ${tono.borde}`, borderLeft: `5px solid ${tono.barra}`,
          borderRadius: 12, padding: '12px 14px', fontFamily: PP, cursor: 'pointer',
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13.5, fontWeight: 700, color: '#2e3338' }}>
          {tono.punto} Vigilante
        </span>
        <span style={{ fontSize: 13, color: '#5e666e' }}>
          {mios.total > 0
            ? `${mios.total} ${mios.total === 1 ? 'expediente necesita' : 'expedientes necesitan'} tu atención`
            : listasRojas > 0
              ? `${listasRojas} ${listasRojas === 1 ? 'línea se vende' : 'líneas se venden'} bajo costo`
              : 'Revisa el resumen de la empresa'}
        </span>
        <span style={{ marginLeft: 'auto', fontSize: 12, color: tono.texto, fontWeight: 600 }}>Ver</span>
      </div>
    );
  }

  const Item = ({ a }) => {
    const t = TONO[a.gravedad] || TONO.gris;
    return (
      <div style={{
        background: '#fff', borderRadius: 10, padding: '10px 12px',
        borderLeft: `5px solid ${t.barra}`, marginBottom: 8,
        display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
      }}>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: '#2e3338', wordBreak: 'break-word' }}>
            {a.asunto}
            <span style={{ fontWeight: 500, color: '#9aa3ab' }}> · {a.folio}</span>
          </div>
          <div style={{ fontSize: 12.5, color: t.texto, fontWeight: 600, marginTop: 2 }}>
            {a.titulo_regla}
            {a.dias_abierto > 0 && (
              <span style={{ fontWeight: 500, color: '#9aa3ab' }}> · avisado hace {a.dias_abierto} d</span>
            )}
            {a.escalado_at && (
              <span style={{ color: '#c0392b', fontWeight: 700 }}> · escalado a dirección</span>
            )}
          </div>
          {a.cliente && <div style={{ fontSize: 12, color: '#9aa3ab' }}>{a.cliente}</div>}
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button style={{ ...boton, background: t.barra, color: '#fff', borderColor: t.barra }}
                  onClick={() => abrirExpediente(a.oportunidad_id)}>Abrir</button>
          <button style={boton} onClick={() => { setPosponiendo({ id: a.id, folio: a.folio }); setError(null); }}>
            Posponer
          </button>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      background: tono.fondo, border: `1px solid ${tono.borde}`, borderRadius: 14,
      padding: isMobile ? 14 : 16, fontFamily: PP, color: tono.texto,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <span style={{ fontSize: 15, fontWeight: 800 }}>{tono.punto} Lo que necesita tu atención</span>
        {mios.total > 0 && (
          <span style={{
            background: '#fff', border: `1px solid ${tono.borde}`, borderRadius: 999,
            padding: '2px 10px', fontSize: 11.5, fontWeight: 700,
          }}>
            {(mios.cuenta.rojo || 0) > 0 && `${mios.cuenta.rojo} grave${mios.cuenta.rojo > 1 ? 's' : ''}`}
            {(mios.cuenta.rojo || 0) > 0 && (mios.cuenta.ambar || 0) > 0 && ' · '}
            {(mios.cuenta.ambar || 0) > 0 && `${mios.cuenta.ambar} por revisar`}
          </span>
        )}
        <button style={{ ...boton, marginLeft: 'auto' }} onClick={marcarVisto}>Ya lo vi</button>
      </div>

      {(todos || mios.items).map(a => <Item key={a.id} a={a} />)}

      {!todos && mios.hay_mas > 0 && (
        <button style={{ ...boton, width: '100%', marginTop: 2 }} onClick={verTodos}>
          Ver los {mios.hay_mas} restantes
        </button>
      )}

      {/* ALERTA-BAJO-COSTO — listas con precio congelado. SOLO direccion (lo decide el servidor). */}
      {lbc && !nadaListas && (
        <div style={{ marginTop: (mios.items.length || todos) ? 14 : 0, paddingTop: (mios.items.length || todos) ? 12 : 0,
                      borderTop: (mios.items.length || todos) ? `1px dashed ${tono.borde}` : 'none' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            Listas con precio congelado
            {listasRojas > 0 && <span style={{ color: TONO.rojo.texto }}> · {listasRojas} bajo costo sin revisar</span>}
            {lbc.margen_resumen.margen_bajo > 0 && <span style={{ fontWeight: 500 }}> · {lbc.margen_resumen.margen_bajo} con margen menor al {lbc.umbral}%</span>}
            {lbc.margen_resumen.perdida_aceptada > 0 && <span style={{ fontWeight: 500, color: '#9aa3ab' }}> · {lbc.margen_resumen.perdida_aceptada} a pérdida aceptada</span>}
          </div>
          {(lbc.sin_revisar || []).map(l => (
            <div key={l.item_id} style={{
              background: '#fff', borderRadius: 10, padding: '10px 12px', borderLeft: `5px solid ${TONO.rojo.barra}`,
              marginBottom: 8, display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap',
            }}>
              <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: '#2e3338', wordBreak: 'break-word' }}>
                  {l.modelo || l.nombre}
                  <span style={{ fontWeight: 500, color: '#9aa3ab' }}> · {l.coleccion_nombre}</span>
                </div>
                <div style={{ fontSize: 12.5, color: TONO.rojo.texto, fontWeight: 600, marginTop: 2 }}>
                  Se vende a {fmtDinero(l.precio_congelado_mxn)} y hoy cuesta {fmtDinero(l.costo_hoy_mxn)}: pierdes {fmtDinero(l.costo_perdida_unidad_mxn)} por pieza
                </div>
              </div>
              <button style={{ ...boton, background: TONO.rojo.barra, color: '#fff', borderColor: TONO.rojo.barra }}
                      onClick={() => abrirLista({ id: l.coleccion_id, nombre: l.coleccion_nombre, congelada: l.coleccion_congelada })}>
                Abrir la lista
              </button>
            </div>
          ))}
          {listasRojas === 0 && (lbc.por_lista || []).map(l => (
            <button key={l.id} style={{ ...boton, marginRight: 6, marginBottom: 6 }} onClick={() => abrirLista(l)}>
              Abrir {l.nombre}
            </button>
          ))}
        </div>
      )}

      {/* Expedientes sin responsable: si no hay a quien avisar, avisa arriba (punto 11) */}
      {huer && huer.total > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${tono.borde}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            Sin responsable asignado ({huer.total})
          </div>
          {huer.items.map(a => <Item key={a.id} a={a} />)}
        </div>
      )}

      {/* Resumen de la empresa — SOLO DIRECCION (el candado esta en el servidor) */}
      {resumen && resumen.por_persona && resumen.por_persona.length > 0 && (
        <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px dashed ${tono.borde}` }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
            Toda la empresa · {resumen.totales.total} pendientes, {resumen.totales.rojos} graves
          </div>
          {resumen.por_persona.map((p, i) => (
            <div key={i} style={{
              fontSize: 13, color: '#2e3338', background: '#fff', borderRadius: 8,
              padding: '8px 10px', marginBottom: 6, wordBreak: 'break-word',
              borderLeft: `4px solid ${p.rojos > 0 ? TONO.rojo.barra : TONO.ambar.barra}`,
            }}>
              {p.frase}
            </div>
          ))}
          {resumen.escalados && resumen.escalados.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6, color: TONO.rojo.texto }}>
                ⚠️ Escalados: nadie los ha resuelto ({resumen.escalados.length})
              </div>
              {resumen.escalados.slice(0, 5).map(e => (
                <div key={e.id} style={{
                  fontSize: 12.5, background: '#fff', borderRadius: 8, padding: '8px 10px',
                  marginBottom: 6, borderLeft: `4px solid ${TONO.rojo.barra}`, cursor: 'pointer',
                }} onClick={() => abrirExpediente(e.oportunidad_id)}>
                  <strong>{e.folio}</strong> {e.asunto} — {e.titulo_regla}
                  <div style={{ color: '#9aa3ab' }}>
                    {e.dias_abierto} días sin resolver · se le avisó a {e.se_le_aviso_a}
                  </div>
                </div>
              ))}
            </div>
          )}
          {resumen.ultima_corrida && (
            <div style={{ fontSize: 11, color: '#9aa3ab', marginTop: 6 }}>
              Última revisión: {new Date(resumen.ultima_corrida.corrio_at).toLocaleString('es-MX')}
            </div>
          )}
        </div>
      )}

      {/* Posponer: SIEMPRE con motivo escrito */}
      {posponiendo && (
        <div style={{
          marginTop: 12, background: '#fff', border: `1px solid ${tono.borde}`,
          borderRadius: 10, padding: 12,
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#2e3338', marginBottom: 6 }}>
            Posponer {posponiendo.folio}
          </div>
          <div style={{ fontSize: 12, color: '#5e666e', marginBottom: 8 }}>
            El aviso vuelve solo cuando pasen los días. Hay que escribir por qué: queda registrado.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {[3, 7, 15, 30].map(d => (
              <button key={d} onClick={() => setDias(d)}
                style={{ ...boton, fontWeight: d === dias ? 800 : 500,
                         background: d === dias ? tono.barra : '#fff',
                         color: d === dias ? '#fff' : tono.texto }}>
                {d} días
              </button>
            ))}
          </div>
          <textarea
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            placeholder="Por qué se pospone (el cliente está de viaje, falta el plano…)"
            rows={2}
            style={{
              width: '100%', boxSizing: 'border-box', fontFamily: PP, fontSize: 13,
              padding: 8, borderRadius: 8, border: '1px solid #e6e8ea', resize: 'vertical',
            }}
          />
          {error && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 6 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <button style={{ ...boton, background: tono.barra, color: '#fff', borderColor: tono.barra }}
                    onClick={confirmarPosponer}>Posponer</button>
            <button style={boton} onClick={() => { setPosponiendo(null); setError(null); }}>Cancelar</button>
          </div>
        </div>
      )}
    </div>
  );
}
